import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { CustomizerContext } from "../context/CustomizerContext";
import { get } from "../helpers/request";
import { convertToY, toSlateFormat } from "../hooks/documentHooks";
import * as Y from 'yjs'
import { slateNodesToInsertDelta } from "@slate-yjs/core";
import cssbeautify from 'cssbeautify';
import { parseFile } from "../helpers/utils";

const dialogStyle = { "& .MuiDialog-paper": { width: "80%", maxHeight: 435 } }

const OverrideWithEnvironment = ({ environment, onClose }) => {
  const currentSite = useSelector((state) => state.site);
  const { document } = useContext(CustomizerContext);
  const [environmentName, setEnvironmentName] = useState(environment);
  const domain = useMemo(() => {
    return environmentName === "production"
      ? currentSite?.domain
      : currentSite?.child_site?.domain;
  }, [environmentName, currentSite?.domain, currentSite?.child_site?.domain]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleOverride = useCallback(() => {
    if (currentSite && document && domain) {
      get(
        `${process.env.REACT_APP_API_URL}/internal/admin/v1/file_loader/${
          currentSite?.site_id
        }/${
          environmentName === "production"
            ? currentSite?.current_version
            : currentSite?.child_site?.current_version
        }/data-theme-datasellers-themesellers-general`
      )
        .then((settings) => {
          // DATA
          const data = document.getMap("data");
          for (let key of data.keys()) {
            data.delete(key);
          }
          Object.entries(parseFile(settings.data)).forEach(([key, value]) => {
            data.set(key, convertToY(value));
          });

          // GENERAL
          const general = document.getMap("general");
          for (let key of general.keys()) {
            general.delete(key);
          }
          Object.entries(JSON.parse(settings.general || "{}")).forEach(
            ([key, value]) => {
              general.set(key, convertToY(value));
            }
          );

          // DATA SELLERS
          const dataSellers = document.getMap("dataSellers");
          for (let key of dataSellers.keys()) {
            dataSellers.delete(key);
          }
          Object.entries(JSON.parse(settings.datasellers || "{}")).forEach(
            ([key, value]) => {
              dataSellers.set(key, convertToY(value));
            }
          );

          const theme = document.get("theme.draft", Y.XmlText);
          if (theme.toString().length > 0) theme.delete(0, Number.MAX_SAFE_INTEGER);
          const themeCss = cssbeautify(settings.theme || '', { indent: '  ' })
          theme.applyDelta(slateNodesToInsertDelta(toSlateFormat(themeCss, 'css')))

          const themeSellers = document.get("themeSellers.draft", Y.XmlText);
          if (themeSellers.toString().length > 0) themeSellers.delete(0, Number.MAX_SAFE_INTEGER);
          const themeSellersCss = cssbeautify(settings.themesellers || '', { indent: '  ' })
          themeSellers.applyDelta(slateNodesToInsertDelta(toSlateFormat(themeSellersCss, 'css')))
        })
        .then(() => onClose())
        .catch((error) => console.error(error));
    }
  }, [currentSite, document, onClose, domain, environmentName]);

  useEffect(() => {
    if (environment) setEnvironmentName(environment);
  }, [environment]);

  return (
    <Dialog
      sx={dialogStyle}
      maxWidth="xs"
      open={!!environment}
    >
      <DialogTitle>{domain ? "Confirmation" : "Error"}</DialogTitle>
      <DialogContent dividers>
        {domain && (
          <DialogContentText>
            Are you sure you want to override current configuration with the
            settings in <strong>{environmentName}</strong> site? ({domain})
          </DialogContentText>
        )}
        {!domain && (
          <DialogContentText>
            The <strong>{environmentName}</strong> site is not configured.
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleCancel}>
          Cancel
        </Button>
        {domain && (
          <Button color="error" onClick={handleOverride}>
            Override
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OverrideWithEnvironment;
